// material-ui
import {
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Theme,
  useMediaQuery
} from '@mui/material';

// project imports
import MainCard from 'components/cards/MainCard';

//types
import { FilterCategory, FilterItem } from 'models/table';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Formik, useFormikContext } from 'formik';
import * as React from 'react';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import i18n from 'i18n';
import { enUS, sk } from 'date-fns/locale';
import moment from 'moment';
import { DateFormat, GRID_SPACING } from 'utils/constants';

type CheckBoxFilterItemPropsType = {
  name: string;
  checked: boolean;
  filterCategoryItem: FilterItem;
  onChange: (e: React.ChangeEvent) => void;
};

const CheckBoxFilterItem = ({
  name,
  checked,
  filterCategoryItem,
  onChange
}: CheckBoxFilterItemPropsType) => {
  return (
    <Grid item xs={12}>
      <FormControlLabel
        control={<Checkbox name={name} onChange={onChange} checked={checked} />}
        label={filterCategoryItem.label}
        key={filterCategoryItem.value}
      />
    </Grid>
  );
};

type RadioButtonFilterItemPropsType = {
  value: string;
  filterCategoryItem: FilterItem;
};

const RadioButtonFilterItem = ({
  value,
  filterCategoryItem
}: RadioButtonFilterItemPropsType) => {
  return (
    <Grid item xs={12}>
      <FormControlLabel
        value={value}
        control={<Radio />}
        label={filterCategoryItem.label}
      />
    </Grid>
  );
};

type DatePickerFilterItemPropsType = {
  value: string;
  filterCategoryItem: FilterItem;
  onChange: (value: string | null, context?: string) => void;
};

const DatePickerFilterItem = ({
  value,
  filterCategoryItem,
  onChange
}: DatePickerFilterItemPropsType) => {
  return (
    <DatePicker
      label={filterCategoryItem.label}
      renderInput={(props) => <TextField fullWidth {...props} helperText='' />}
      value={value}
      inputFormat={DateFormat.BUSINESS_DAY_FORMAT}
      onChange={onChange}
    />
  );
};

type PossibleFormValuesType = boolean | string | null;

export type FormikValuesType = { [key: string]: PossibleFormValuesType };

type PossibleFilterStateValueType = string | string[] | null;

export type FilterStateType = {
  [key: string]: PossibleFilterStateValueType;
};

type FilterContentPropsType = {
  categories: FilterCategory[];
  filterState?: FilterStateType;
  onSubmit: (values: FilterStateType) => void;
};

export const formikCategoryDelimiter = '___';

const getFormikItemName = (categoryName: string, itemName: string) => {
  return `${categoryName}${formikCategoryDelimiter}${itemName}`;
};

type FilterCategoryItemPropsType = {
  filterCategory: FilterCategory;
  onChange: (e: React.ChangeEvent) => void;
};

const FilterCategoryItem = ({
  filterCategory,
  onChange
}: FilterCategoryItemPropsType) => {
  const { values, setFieldValue, handleSubmit } =
    useFormikContext<FormikValuesType>();

  const getFormikValue = (formikItemName: string) => {
    return values[formikItemName];
  };

  let elementToReturn = <></>;

  switch (filterCategory.type) {
    case 'checkbox':
      elementToReturn = (
        <>
          {filterCategory.filterCategoryItems.map((filterCategoryItem) => {
            const name = getFormikItemName(
              filterCategory.name,
              filterCategoryItem.value
            );

            const checked = getFormikValue(name) as boolean;

            return (
              <CheckBoxFilterItem
                key={name}
                name={name}
                filterCategoryItem={filterCategoryItem}
                checked={checked}
                onChange={onChange}
              />
            );
          })}
        </>
      );
      break;
    case 'radioButton':
      elementToReturn = (
        <RadioGroup
          value={values[filterCategory.name]}
          row
          aria-label={filterCategory.name}
          name={filterCategory.name}
          onChange={onChange}
        >
          {filterCategory.filterCategoryItems.map((filterCategoryItem) => {
            const name = getFormikItemName(
              filterCategory.name,
              filterCategoryItem.value
            );

            return (
              <RadioButtonFilterItem
                key={name}
                value={filterCategoryItem.value}
                filterCategoryItem={filterCategoryItem}
              />
            );
          })}
        </RadioGroup>
      );
      break;
    case 'datePicker':
      elementToReturn = (
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={i18n.language === 'en' ? enUS : sk}
        >
          <Stack direction={'column'} spacing={GRID_SPACING}>
            {filterCategory.filterCategoryItems.map((filterCategoryItem) => {
              const name = getFormikItemName(
                filterCategory.name,
                filterCategoryItem.value
              );

              const value = values[name];

              const handleDatePickerChange = (
                newValue: string | null,
                keyboardInputValue?: string
              ) => {
                setFieldValue(name, newValue, false);

                if (
                  !keyboardInputValue ||
                  moment(
                    keyboardInputValue,
                    DateFormat.DATE_FORMAT,
                    true
                  ).isValid()
                ) {
                  handleSubmit();
                }
              };

              return (
                <DatePickerFilterItem
                  key={name}
                  value={value as string}
                  filterCategoryItem={filterCategoryItem}
                  onChange={handleDatePickerChange}
                />
              );
            })}
          </Stack>
        </LocalizationProvider>
      );
      break;
  }

  return elementToReturn;
};

const FilterContent = ({
  categories,
  onSubmit,
  filterState
}: FilterContentPropsType) => {
  const theme = useTheme();

  const matchDownLG = useMediaQuery((mediaQueryTheme: Theme) =>
    mediaQueryTheme.breakpoints.down('xl')
  );

  const [expandedAccordions, setExpandedAccordions] = useState<string[]>([]);

  const handleAccordionChange = (id: string) => {
    let newExpandedAccordions: string[] = [];
    if (expandedAccordions.includes(id)) {
      newExpandedAccordions = expandedAccordions.filter(
        (expandedAccordionId) => expandedAccordionId !== id
      );
    } else {
      newExpandedAccordions = [...expandedAccordions, id];
    }

    setExpandedAccordions(newExpandedAccordions);
  };

  const createFormikValues = (): FormikValuesType | undefined => {
    if (filterState) {
      const formikValues: FormikValuesType = {};

      categories.forEach((category) => {
        const valuesOfCategory = filterState[category.name];

        switch (category.type) {
          case 'checkbox':
            category.filterCategoryItems.forEach((filterCategoryItem) => {
              const newValueName = `${category.name}${formikCategoryDelimiter}${filterCategoryItem.value}`;
              const newValueValue = !!(valuesOfCategory as string[])?.find(
                (valueOfCategory) =>
                  valueOfCategory === filterCategoryItem.value
              );

              formikValues[newValueName] = newValueValue;
            });
            break;
          case 'radioButton':
            formikValues[category.name] = valuesOfCategory as string;
            break;
          case 'datePicker':
            category.filterCategoryItems.forEach((filterCategoryItem) => {
              const newValueName = `${category.name}${formikCategoryDelimiter}${filterCategoryItem.value}`;

              formikValues[newValueName] = filterState[
                filterCategoryItem.value
              ] as string;
            });
            break;
        }
      });

      return formikValues;
    }
  };

  const formikValues = createFormikValues();

  const handleSubmitFormik = (newFormikValues: FormikValuesType) => {
    const newFilterState: FilterStateType = {};
    Object.keys(newFormikValues).forEach((formikValueKey) => {
      const filterItemCategoryValuePair = formikValueKey.split(
        formikCategoryDelimiter
      );

      const category = filterItemCategoryValuePair[0];
      const value = filterItemCategoryValuePair[1];

      const categoryType = categories.find(
        (categoryItem) => categoryItem.name === category
      )?.type;

      if (categoryType) {
        switch (categoryType) {
          case 'checkbox':
            if (!newFilterState[category]) {
              newFilterState[category] = [];
            }
            if (newFormikValues[formikValueKey] === true) {
              (newFilterState[category] as string[]).push(value);
            }
            break;
          case 'radioButton':
            newFilterState[category] = newFormikValues[
              formikValueKey
            ] as string;
            break;
          case 'datePicker':
            newFilterState[value] = newFormikValues[formikValueKey] as string;
            break;
        }
      }
    });

    onSubmit(newFilterState);
  };

  return (
    <MainCard
      border={!matchDownLG}
      content={false}
      sx={{ overflow: 'visible' }}
    >
      {formikValues && (
        <CardContent sx={{ p: 1, height: 'auto' }}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Box sx={{ width: '100%' }}>
                <Formik
                  initialValues={formikValues}
                  onSubmit={handleSubmitFormik}
                >
                  {({ handleChange, handleSubmit }) => {
                    const handleInputChange = (e: React.ChangeEvent) => {
                      handleChange(e);
                      handleSubmit();
                    };

                    return (
                      <form onSubmit={handleSubmit}>
                        {categories.map((filterCategory) => (
                          <MuiAccordion
                            key={filterCategory.name}
                            defaultExpanded={true}
                            onChange={() => {
                              handleAccordionChange(filterCategory.name);
                            }}
                          >
                            <MuiAccordionSummary
                              sx={{
                                color:
                                  theme.palette.mode === 'dark'
                                    ? 'grey.500'
                                    : 'grey.800',
                                fontWeight: 500
                              }}
                              expandIcon={<ExpandMoreIcon />}
                            >
                              {filterCategory.label}
                            </MuiAccordionSummary>
                            <MuiAccordionDetails>
                              <Grid container spacing={1}>
                                <Grid item xs={12}>
                                  <Grid container spacing={1}>
                                    <FilterCategoryItem
                                      filterCategory={filterCategory}
                                      onChange={handleInputChange}
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>
                            </MuiAccordionDetails>
                          </MuiAccordion>
                        ))}
                      </form>
                    );
                  }}
                </Formik>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      )}
    </MainCard>
  );
};

export default FilterContent;
